<template>
  <div>
    <Row :gutter="20" type="flex" class="grid-list-row mx-0"
      :class="{ 'alert-list alert-orange': showAlert, 'alert-list alert-red': (errorMessage.length || showEpodRejectAlert) }">

      <!-- Alert Badges -->
      <Alert
        size="large"
        class="alert-md"
        :closable="false"
        v-if="showAlert">
          <svgicon class="alert-icon" name="info" width="4" height="12"/>
          <span v-if="showSlaAcceptanceBreachIn">{{$t('INDENT_ACCEPTANCE_SLA_BREACH_IN')}}</span>
          <span v-else>{{$t('INDENT_ARRIVAL_SLA_BREACH_IN')}}</span>
           {{hours}}:{{minutes}}:{{seconds}}
      </Alert>

      <Alert
        size="large"
        class="alert-md"
        :closable="false"
        v-if="showEpodRejectAlert">
          {{$t('INDENT_DAMAGE_REJECT')}}
      </Alert>

      <Alert
        v-if="errorMessage.length"
        size="large"
        type="info"
        width="auto"
        trigger="click"
        :closable="false">
          <span
            v-for="(error, errorIndex) in errorMessage"
            :key="'erroritems' + errorIndex">
              {{error}}
          </span>
      </Alert>

      <!-- List Items -->
      <Col :span="column.colSpan" class="pointer text-left" align="middle"
        v-for="(column, index) in sectionHeader"
        :key="index"
        @click.native="showDetails">
          <span v-if="!isReturnIndent && hasChildIndents">
              <span class="row-expand" :class="{'is-toggle': isExpandView}"
              @click="toggleExpandView" />
          </span>
          <span class="status status-icon-bottom"
            :class="typeof getStatusClass(getIndentDetails[column.label]) === 'string' ? getStatusClass(getIndentDetails[column.label]) : ''"
            :style="typeof getStatusClass(getIndentDetails[column.label]) === 'object' ? getStatusClass(getIndentDetails[column.label]) : {}"
            v-if="column.label === 'status'">
            {{$t($statusUtil.getMainStatusObj(getIndentDetails[column.label]).tlabel)}}
            <Tooltip class="item" effect="dark" placement="bottom">
              <div slot="content" class="tooltipcontent">
                <span v-if="getOrderType">{{getOrderType.label}}</span>
                <span v-if="getMovement">|{{getMovement.name}}</span>
                <span v-if="getVehicleType">|{{getVehicleType.name}}</span>
              </div>
              <span class="badge badge-bottom">
                <svgicon v-if="getOrderType" :name="getOrderType.icon" width="8" height="8"/>
                <svgicon v-if="getMovement" :name="getMovement.icon" width="8" height="8" />
                <svgicon v-if="getVehicleType && getVehicleType.icon"
                  :name="getVehicleType.icon" width="8" height="8" />
              </span>
            </Tooltip>
          </span>

          <p v-if="!['status'].includes(column.label)">
            <Ellipsis
              v-if="column.expanded_text"
              :text="rowLabel(indent, column)"
              :length="column.truncate_on"
              :expandedContext="rowLabel(indent, column.expanded_text, true)"/>
            <Ellipsis
              v-else
              :text="rowLabel(indent, column)"
              :length="column.truncate_on"/>
            <small v-if="column.dependency && column.dependency.is_enabled">
              <Ellipsis
                :text="rowLabel(indent, column.dependency)"
                :length="column.dependency.truncate_on"/>
            </small>
          </p>
      </Col>

      <!-- Action Items -->
      <Col align="middle" class="col-push text-right">
        <!--  CONTAINER PICKED -->
        <span  v-if="showContainerBooked" >
          <span
            class="link-action"
            v-if="isMasterIndentActionCompleted"
            @click="showActions({ action: transporterActions.container_pickup })">
          <svgicon name="icon_vehicle" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_CONTAINER_PICKUP')}}</span>
          </span>
          <span
            class="link-action action-disabled" v-else>
            <Tooltip
              popper-class="max-width"
              :open-delay="200"
              effect="dark"
              placement="bottom-start">
                <div slot="content">{{$t('INDENT_CONTAINER_NOT_BOOKED')}}</div>
                <span  class="pointer">
                  <svgicon name="icon_vehicle" width="10" height="10" />
                  <span v-if="isFilterClosed">{{$t('INDENT_CONTAINER_PICKUP')}}</span>
                </span>
            </Tooltip>
          </span>
        </span>

        <!-- Requsted for truck -->
        <span
          class="link-action text-red1 default-cursor"
          v-if="showChangeRequestedStatus">
          <svgicon name="link" width="10" height="10"  />
          <span v-if="isFilterClosed">{{$t('INDENT_CHANGE_REQUESTED')}}</span>
        </span>
         <!-- Requsted for freight rate change -->
         <span
          class="link-action text-red1 default-cursor"
          v-if="hasFreightChangeRequested">
          <svgicon name="link" width="10" height="10"  />
          <span v-if="isFilterClosed">{{$t('INDENT_FREIGHT_CHANGE_REQUESTED')}}</span>
        </span>
        <!-- ACCEPT in slider if it is PTL-->
        <span
          class="link-action"
          v-if="[0, 21].includes(indent.status) && !isReturn && getVehicleType.id === vehicleTypes[1].id && this.$hasFeature('ptl_grouping') && !hasFreightChangeRequested"
          @click="showActions({ ga: 'Indent accepted', action: transporterActions.accept })">
          <svgicon name="accept" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_ACCEPT')}}</span>
        </span>
        <!-- Workflow Based ACTIONS-->
        <span
          class="link-action" v-if="isShipmentWorkflowEnabled && indent.next_event
            && showWorkflowActions && indent.status !== 0"
          @click="openWorkflowActions()">
          <svgicon name="icon-inspection" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('ACTIONS')}}</span>
        </span>
        <!-- Last mile actions -->
        <span
          class="link-action"
          v-if="isLastMileEnabled && showLastMileActions &&
            (!isShipmentWorkflowEnabled || !indent.next_event)"
          @click="showActions({ action: transporterActions.last_mile_actions })">
          <svgicon name="icon-inspection" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('ACTIONS')}}</span>
        </span>

        <!-- ACCEPT if it is FTL Indent -->
        <span
          class="link-action"
          v-if="[0, 21].includes(indent.status) && (!this.$hasFeature('ptl_grouping') || getVehicleType.id === vehicleTypes[0].id || isReturn) && !hasFreightChangeRequested" @click="$emit('accept-indent');
            $emit('setGA','Indent accepted', 'Click', 'Indent accept');">
          <svgicon name="accept" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_ACCEPT')}}</span>
        </span>
        <!-- REJECT -->
        <span
          class="link-action"
          v-if="isRejectAllowed && !hasFreightChangeRequested"
          @click="showActions({ ga: 'Indent rejected', action: transporterActions.reject })">
          <svgicon name="reject" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_REJECT')}}</span>
        </span>
         <!-- Mark As Delivered -->
        <span
          class="link-action"
          v-if="!isLastMileEnabled && isAllowedForMarkAsDelivered
            && $statusUtil.isDispatched(indent.status) &&
            (!isShipmentWorkflowEnabled || !indent.next_event)"
          @click="showActions({ action: transporterActions.mark_as_delivered })">
          <svgicon name="mark-as-delivered" width="10" height="10" />
          {{$t('INDENT_MARK_AS_DELIVERED')}}
        </span>
        <!-- Truck In -->
        <span
          class="link-action"
          v-if="!isLastMileEnabled && truckInEnable &&
            (!isShipmentWorkflowEnabled || !indent.next_event)"
          @click="showActions({ action: transporterActions.truck_in })">
          <svgicon name="icon-truckin" width="10" height="10" />
          {{$t('INDENT_TRUCK_IN')}}
        </span>
        <!-- Truck Out -->
        <span
          class="link-action"
          v-if="!isLastMileEnabled && truckOutEnable && truckOutDisableCheck &&
            (!isShipmentWorkflowEnabled || !indent.next_event)" @click="showActions(
            { action: transporterActions.truck_out })">
          <svgicon name="icon-truckout" width="10" height="10" />
          {{$t('INDENT_TRUCK_OUT')}}
        </span>
        <span class="link-action action-disabled" v-else-if="!isLastMileEnabled && truckOutEnable
          && (!isShipmentWorkflowEnabled || !indent.next_event)">
          <svgicon name="icon-truckout" width="10" height="10" />
          {{$t('INDENT_TRUCK_OUT')}}
        </span>
        <!-- ASSIGN TRUCK If order is indented -->
        <span
          class="link-action"
          v-if="[3, 20].includes(indent.status) && !isReturnIndent &&
            !(indent.vehicle && indent.vehicle.number) &&
            (!isShipmentWorkflowEnabled || !indent.next_event)"
           @click="showActions(
            { ga: 'vehicle assigned', action: transporterActions.assign_truck, })">
          <svgicon name="icon_vehicle" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_ASSIGN_TRUCK')}}</span>
        </span>
        <!-- CHANGE TRUCK TYPE-->
        <span
          class="link-action"
          v-if="showChangeAction"
          @click="showActions({ action: transporterActions.change_truck_type })">
          <svgicon name="icon_vehicle" width="10" height="10" />
          <span v-if="isFilterClosed">{{$t('INDENT_CHANGE')}}</span>
        </span>
        <!-- CHANGE TRUCK-->
        <span
          class="link-action"
          v-if="indent.vehicle && indent.vehicle.number && !isReturnIndent"
          @click="showActions({ action: transporterActions.vehicle_details })">
          <svgicon name="icon_vehicle" width="10" height="10" />
          <span v-if="isFilterClosed">{{indent.vehicle.number}}</span>
        </span>
        <!-- Documents -->
         <div class="link-action" :class="lrColor"
         v-if="showDocuments">
          <router-link :to="{ name: 'DocumentDetails', params: { orderNo: indent.order_no }}">
            <svgicon name="icon-document" width="10" height="9" />
            <span v-if="isFilterClosed">{{$t('INDENT_DOCUMENT')}}</span>
          </router-link>
        </div>
        <!-- POD AI Retrigger -->
        <span
          class="link-action text-red1"
          v-if="$hasFeature('pod_rejection_by_ai') && hasAiRejectedDestination"
          @click.stop="openResubmitDialog">
          <svgicon name="icon-re-indent" width="10" height="10" />
          <span v-if="isFilterClosed">Retry POD</span>
        </span>
      </Col>
    </Row>
    <row
      v-if="!isReturnIndent && hasChildIndents && isExpandView"
      :sectionHeader="sectionHeader"
      :isReturnIndent="true"
      :indent="indent.returnIndents"
      :movementTypes="movementTypes"
      :orderTypes="orderTypes"
      :isFilterClosed="isFilterClosed"
      :configurations="configurations"
      :mapConfig="mapConfig"
      @show-details="$emit('show-details',indent.returnIndents.order_no, true, hasChildIndents)"
      @show-actions="$emit('show-actions',indent.returnIndents.order_no, true, hasChildIndents)"
      class="nested-list">
    </row>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import {
  orderTypes,
  movementTypes,
  vehicleTypes,
  indentsStatus,
  transporterActions, deliveryPendingReasonsMapping } from '@/constants/indentConstants';
import { importExportType, Movement, epodApprovalStatus, statusIdDictionary, commonApprovalStatus } from 'pando-constants';
import getRoute from '@/lib/Route';
import moment from 'moment';
import { uniqBy } from 'lodash';

export default {
  name: 'row',
  props: {
    depot_id: {
      type: String,
      default: '',
    },
    indent: {
      type: Object,
      default: () => {},
    },
    sectionHeader: {
      type: Array,
      default: () => [],
    },
    isFilterClosed: {
      type: Boolean,
      default: true,
    },
    isReturnIndent: {
      type: Boolean,
      default: false,
    },
    configurations: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isExpandView: false,
      currentTimeMilliSeconds: Math.trunc((new Date()).getTime() / 1000),
      orderTypes,
      movementTypes,
      vehicleTypes,
      getRoute,
      indentsStatus,
      containerPickup: false,
      transporterActions,
      epodApprovalStatus,
    };
  },
  mounted() {
    window.setInterval(() => {
      this.currentTimeMilliSeconds = Math.trunc((new Date()).getTime() / 1000);
    }, 1000);
  },
  computed: {
    ...mapGetters({
      currentUser: 'app/currentUser',
      clientConfigurations: 'app/currentClientConfig',
    }),
    hasFreightChangeRequested() {
      return this.indent.freight_change_requested &&
        this.indent.status === this.$statusUtil.getStatusId('freight_change_requested');
    },
    isLastMileEnabled() {
      return this.$hasFeature('last_mile');
    },
    isReturn() {
      return this.isReturnIndent || ![Movement.PRIMARY, Movement.SECONDARY].includes(this.indent.movement);
    },
    showLastMileActions() {
      const invalidStatuses = this.$statusUtil.getStatusIds(['indented', 'truck_change_requested', 'indent_rejected',
        'indent_accepted', 'indent_accept_due', 'indent_assign_due', 'requested', 'drafted', 'cancelled']);
      return !invalidStatuses.includes(this.indent.status);
    },
    mapConfig() {
      return (this.configurations && this.configurations.map_configuration);
    },
    timeFormat() {
      return (this.clientConfigurations && this.clientConfigurations.is_datepicker_24H_enabled) ? 'DD-MM-yyyy HH:mm' : 'DD-MM-yyyy hh:mm A';
    },
    isRejectAllowed() {
      let validStatuses;
      if (this.isSlaBreached) {
        validStatuses = ['indented', 'indent_accept_due'];
      } else {
        validStatuses = ['indented', 'truck_change_requested',
          'indent_accepted', 'truck_assigned',
          'requested', 'reported', 'truck_rejected'];
      }
      return this.$statusUtil.getStatusIds(validStatuses).indexOf(this.indent.status) >= 0 &&
        !this.isReturnIndent;
    },
    showContainerBooked() {
      return this.indent && this.indent.container_id &&
        [4, 22].includes(this.indent.status) && this.indent.status !== 26 &&
          this.indent.master_indent.exim_type === importExportType.EXPORT;
    },
    hasChildIndents() {
      return this.indent && this.indent.child_indent_ids && this.indent.child_indent_ids.length;
    },
    truckInEnable() {
      const movementType = this.movementTypes.find(val => val.id === this.indent.movement);
      return (this.configurations.indent_configuration_settings.transporter_truckin &&
          this.configurations.indent_configuration_settings
            .transporter_truckin[movementType.name.toLowerCase()]) &&
          ((this.indent.status === 5 && this.insideGate) ||
          (([4, 22, 24].includes(this.indent.status) &&
          this.$hasFeature('reported_at_depot')))
          || (this.indent.status === 4 && !this.$hasFeature('reported_at_depot')));
    },
    isAllowedForMarkAsDelivered() {
      const movementType = this.movementTypes.find(val => val.id === this.indent.movement);
      return this.configurations.is_grn_enabled_for_transporter &&
        this.configurations.is_grn_enabled_for_transporter[movementType.name.toLowerCase()];
    },
    getIndentDetails() {
      const indentDetails = {
        order_no: this.indent.order_no,
        created_at: moment(this.indent.created_at).format('DD MMM YYYY'),
        status: this.getMainStatusId(this.indent.status),
        route: getRoute(this.indent.sources, this.indent.destinations),
        vehicle_type: this.indent.vehicleType && this.indent.vehicleType.short_form,
        vechile_req_date: moment(this.indent.sources.reduce((a, b) => (a.vehicle_req_date >
          b.vehicle_req_date ? a : b)).vehicle_req_date).format(this.timeFormat),
      };
      return indentDetails;
    },
    getOrderType() {
      return this.orderTypes.find(val => val.id === this.indent.order_type);
    },
    getMovement() {
      return this.movementTypes.find(val => val.id === this.indent.movement);
    },
    getVehicleType() {
      return this.vehicleTypes.find(val => val.id === this.indent.vehicleType.vehicle_type);
    },
    getVehicleTypeId() {
      return this.vehicleTypes.find(val => val.id === this.indent.vehicleType.vehicle_type.ID);
    },
    dateInMilliseconds() {
      return Math.trunc(Date.parse(this.indent.transporter_eta) / 1000);
    },
    seconds() {
      let result = (this.dateInMilliseconds - this.currentTimeMilliSeconds) % 60;

      if (result < 0) result = 0;
      return result;
    },
    minutes() {
      let result = Math.trunc((this.dateInMilliseconds - this.currentTimeMilliSeconds) / 60) % 60;

      if (result < 0) result = 0;
      return result;
    },
    hours() {
      let dayToHours = 0;
      if (this.days) {
        dayToHours = this.days * 24;
      }
      let result = dayToHours + (Math.trunc((this.dateInMilliseconds - this.currentTimeMilliSeconds)
        / 60 / 60) % 24);

      if (result < 0) result = 0;
      return result;
    },
    days() {
      let result = Math.trunc((this.dateInMilliseconds - this.currentTimeMilliSeconds) / 60 / 60 / 24);

      if (result < 0) result = 0;
      return result;
    },
    isContract() {
      return this.indent.order_type === 0;
    },
    isIndented() {
      return this.$statusUtil.getMainStatusId(this.indent.status) === 0;
    },
    showDocuments() {
      const statusList = this.$statusUtil.getStatusIds(['indent_rejected', 'cancelled']); // 2 - REJECTED || 8 - CANCELLED
      return !statusList.includes(this.$statusUtil.getMainStatusId(this.indent.status));
    },
    isSlaBreached() {
      return this.indent.transporter_eta
      && (this.dateInMilliseconds < this.currentTimeMilliSeconds);
    },
    showAlert() {
      if (this.configurations.ignore_reported_status_for_sla_check) {
        return [0, 3, 4].includes(this.indent.status) && this.indent.transporter_eta && this.isContract
      && (this.seconds > 0 || this.minutes > 0 || this.hours > 0);
      }
      return [0, 3, 4, 24].includes(this.indent.status) && this.indent.transporter_eta && this.isContract
        && (this.seconds > 0 || this.minutes > 0 || this.hours > 0);
    },
    showSlaAcceptanceBreachIn() {
      return this.indent.status === statusIdDictionary.indented;
    },
    showWorkflowActions() {
      let showAction = true;
      switch (this.indent && this.indent.next_event) {
        case 'VEHICLE_ASSIGNMENT': {
          if (!([3, 20].includes(this.indent.status) && !this.isReturnIndent &&
          !(this.indent.vehicle && this.indent.vehicle.number))) {
            showAction = false;
          }
          break;
        }
        case 'TRUCK_IN':
          if (!this.truckInEnable) {
            showAction = false;
          }
          break;
        case 'TRUCK_OUT': {
          if (!(this.truckOutEnable && this.truckOutDisableCheck)) {
            showAction = false;
          }
          break;
        }
        case 'BOOKING_CONFIRMATION': {
          showAction = true;
          break;
        }
        case '': {
          if (!(this.isAllowedForMarkAsDelivered &&
            this.$statusUtil.isDispatched(this.indent.status))) {
            showAction = true;
          }
          break;
        }
        default:
          break;
      }
      return showAction;
    },
    showEpodRejectAlert() {
      const destinations = this.indent && this.indent.destinations;
      return destinations.some(destination => (destination.epod_rejected) && (!destination.is_transporter_updated));
    },
    hasAiRejectedDestination() {
      return this.indent.pod_ai_overall_status === 'AI_REJECTED';
    },
    firstAiRejectedDestination() {
      const destinations = this.indent && this.indent.destinations;
      return destinations && destinations.find(d => !d.pod_ai_approved);
    },
    lrColor() {
      const orderStatus = this.indent.status;
      let lrStatus;
      if (this.indent.lorry_receipt) {
        lrStatus = 'green1';
      } else {
        const check = this.$statusUtil.isAfterDispatched(orderStatus) ||
          this.$statusUtil.isDispatched(orderStatus) ||
          (orderStatus === this.$statusUtil.getStatusId('loading_ended'));
        lrStatus = check ? 'red1' : '';
      }
      return lrStatus ? `text-${lrStatus}` : '';
    },
    isIndentHasHillyRouteCharge() {
      return this.indent && this.indent.destinations.reduce((acc, row) =>
        acc + row.contract.hilly_region_addition_percentage, 0) > 0;
    },
    showChangeRequestedStatus() {
      return this.isIndented && this.indent.status === 1 &&
        (this.indent.change_truck_type || this.indent.change_hilly_region) &&
        !this.isReturnIndent;
    },
    showChangeAction() {
      return this.isContract &&
        [0, 21].includes(this.indent.status) &&
        !this.indent.change_requested &&
        !this.hasChildIndents &&
        !this.isReturnIndent &&
        ((this.$hasFeature('transporter_change_truck_type') && !this.indent.change_truck_type) ||
        (this.isIndentHasHillyRouteCharge && !this.indent.change_hilly_region));
    },
    isMasterIndentActionCompleted() {
      let response = true;
      if ('master_action_completed' in this.indent) {
        response = this.indent.master_action_completed;
      }
      return response;
    },
    currentGate() {
      return this.indent.sources.find(gate => gate.arrived_at !== null
        && gate.dispatched_at === null);
    },
    truckOutEnable() {
      const movementType = this.movementTypes.find(val => val.id === this.indent.movement);
      return this.$statusUtil.getStatusIds(['truck_arrived',
        'loading_started', 'loading_ended']).includes(this.indent.status) &&
        (this.configurations && this.configurations.indent_configuration_settings
        && this.configurations.indent_configuration_settings.transporter_truckout
        && this.configurations.indent_configuration_settings.transporter_truckout[movementType.name.toLowerCase()])
        && this.currentGate;
    },
    insideGate() {
      const condition =
        this.indent.sources.some(gate => gate.arrived_at !== null && gate.dispatched_at === null);
      return !condition;
    },

    isTruckWithMaterialInvoiceEnabled() {
      const movementType = this.movementTypes.find(val => val.id === this.indent.movement);
      return this.configurations && this.configurations.material_invoice_before_truckout &&
        this.configurations.material_invoice_before_truckout[movementType.name.toLowerCase()];
    },
    truckOutDisableCheck() {
      const currentGate = this.currentGate;
      let gateWiseLrCheck = true;
      let enabledTruckOut = true;
      if (currentGate && this.isTruckWithMaterialInvoiceEnabled) {
        enabledTruckOut = currentGate.is_material_invoice_updated || false;
      }
      if (currentGate && this.$hasFeature('lr_for_truck_out')) {
        const sourceType = currentGate.gate_id ? 'source_gate_id' : 'source_location_id';
        const currentGateType = currentGate.gate_id ? 'gate_id' : 'location_id';

        let consigneeIds = this.indent.destinations
          .filter(consignee => consignee[sourceType] === currentGate[currentGateType])
          .map(consignee => consignee.location_id);
        let consigneeHasLr = uniqBy(this.indent.lorry_receipt, 'destination_location_id')
          .filter(val => consigneeIds.includes(val.destination_location_id));
        if (this.configurations && this.configurations.multi_drop_at_gate_level) {
          consigneeIds = this.indent.destinations
            .filter(consignee => consignee.source_location_id === currentGate.location_id)
            .map((consignee) => {
              if (consignee.gate_id) {
                return `${consignee.location_id}_${consignee.gate_id}`;
              }
              return consignee.location_id;
            });
          consigneeHasLr = uniqBy(this.indent.lorry_receipt,
            item => [item.destination_location_id, item.gate_id]);
        }
        gateWiseLrCheck = currentGate.lr_available || consigneeHasLr.length === [...new Set(consigneeIds)].length;
        if (this.isStorageLocation) {
          const groupedStorage = this.mappedGates.find(gate => (gate && gate.is_storage_location && gate.combinedStorageGateIds.includes(currentGate.gate_id)) || (currentGate.gate_id === gate.id));
          gateWiseLrCheck = (groupedStorage && groupedStorage.lr_available) || consigneeHasLr.length === [...new Set(consigneeIds)].length;
        }
      }
      return (enabledTruckOut && gateWiseLrCheck);
    },
    mappedGates() {
      const listGates = this.indent.sources.filter(
        gate => gate.arrived_at !== null && gate.dispatched_at === null,
      );
      const gates = listGates.map((lgt) => {
        const currentGate = this.$depot.gate(lgt.gate_id, lgt.location_id);
        return {
          ...lgt,
          ...currentGate,
        };
      });
      const { groupedStorage, nonStorageItems } = gates.reduce(
        (acc, item) => {
          if (item.is_storage_location) {
            const locationId = item.location_id || '';
            if (!acc.groupedStorage[locationId]) {
              acc.groupedStorage[locationId] = {
                items: [],
                names: [],
                ids: [],
                lrAvailable: [],
              };
            }
            acc.groupedStorage[locationId].items.push(item);
            acc.groupedStorage[locationId].names.push(item.name || '');
            acc.groupedStorage[locationId].lrAvailable.push(item.lr_available);
            acc.groupedStorage[locationId].ids.push(item.gate_id); // collect storage items
          } else {
            acc.nonStorageItems.push(item); // collect non-storage items
          }
          return acc;
        },
        {
          groupedStorage: {},
          nonStorageItems: [],
        },
      );
        // Combine storage groups per location_id
      const combinedStorageObjects = Object.values(groupedStorage).map(group => ({
        ...group.items[0], // pick first as base
        lr_available: group.lrAvailable.every(Boolean),
        combinedStorageGates: `${group.names.join(', ')} ( Storage Location )`,
        combinedStorageGateIds: group.ids.join(','),
      }));

      return [...combinedStorageObjects, ...nonStorageItems];
    },
    isStorageLocation() {
      return this.$hasFeature('enable_storage_location') || false;
    },
    errorMessage() {
      const error = [];
      const destinations = this.indent.destinations;
      const epodApprovalEventStatus = this.indent.epod_approval_status;
      if (this.indent.track_error && ![this.$statusUtil.getStatusId('delivery_pending'),
        this.$statusUtil.getStatusId('delivered')].includes(this.indent.status)) {
        error.push(this.indent.track_error);
      }
      if (this.indent.status === this.$statusUtil.getStatusId('dispatched')
          && destinations.some(dest => Object.prototype.hasOwnProperty.call(dest, 'is_cash_collected') && !dest.is_cash_collected)) {
        error.push('COD confirmation pending');
      }
      if (this.indent.auto_deliver && this.indent.status === this.$statusUtil.getStatusId('delivery_pending') &&
        !([4, 6].includes(this.indent.delivery_pending_reason)) &&
        epodApprovalEventStatus === this.epodApprovalStatus.PENDING) {
        error.push('GRN Approval Pending');
      }
      if (this.indent.delivery_pending_reason
        && deliveryPendingReasonsMapping[this.indent.delivery_pending_reason]
        && this.indent.status === this.$statusUtil.getStatusId('delivery_pending')) {
        error.push(deliveryPendingReasonsMapping[this.indent.delivery_pending_reason]);
      }
      if (this.$hasFeature('pod_rejection_by_ai') && this.indent.pod_ai_overall_status === 'AI_REJECTED'
        && ![this.$statusUtil.getStatusId('delivered'), this.$statusUtil.getStatusId('cancelled')].includes(this.indent.status)) {
        error.push('POD Rejected by AI');
      }
      return error;
    },
    isShipmentWorkflowEnabled() {
      return this.$hasFeature('shipment_workflow');
    },
  },
  methods: {
    rowLabel(order, label, istooltip = false) {
      if (label && label.data) {
        if (label.data && label.data.key === 'route') {
          if (istooltip) {
            return this.getIndentDetails.route.tooltipText;
          }
          return this.getIndentDetails.route.value;
        }
        if (label.data && label.data.key === 'vehicle_type') {
          return this.getIndentDetails.vehicle_type;
        }
        if (label.data && label.data.key === 'vechile_req_date') {
          return this.getIndentDetails.vechile_req_date;
        }
        let labelValue = order[label.data.key];
        if (label.data.view_rule) {
          labelValue = label.data.view_rule(order[label.data.key]);
          if (label.data.key === 'sources') {
            labelValue = label.data.view_rule(order[label.data.key], this.timeFormat);
          }
        }
        return labelValue || '-';
      } return '-';
    },
    showActions(data) {
      const { ga, action } = data || {};
      this.$emit('getPtl', { order: this.indent, action });
      this.$emit('show-actions', this.indent.order_no, this.isReturnIndent, this.hasChildIndents, action);
      if (ga) {
        this.$emit('setGA', ga, 'Click');
      }
      this.isExpandView = false;
    },
    showDetails() {
      const action = 'INDENT_DETAILS';
      this.$emit('getPtl', { order: this.indent, action });
      this.$emit('show-details', this.indent.order_no, this.isReturnIndent, this.hasChildIndents);
      this.isExpandView = false;
    },
    toggleExpandView(e) {
      e.stopPropagation();
      if (this.isExpandView) {
        this.isExpandView = false;
      } else if (this.indent.returnIndents && this.indent.returnIndents.length) {
        this.isExpandView = true;
      } else {
        this.fetchReturnIndents();
      }
    },
    async fetchReturnIndents() {
      try {
        const params = {
          is_child_indent_query: true,
          indent_ids: this.indent.child_indent_ids,
          page: 1,
        };
        const response = await this.$store.dispatch('indents/getReturnIndents',
          { masterOrderId: this.indent._id, params });
        if (response && response.error) {
          this.$error({ message: response.error });
        } else {
          this.isExpandView = true;
        }
      } catch (error) {
        this.$errorMessage({ message: this.$getErr('APP_CATCH_ERR') });
      }
    },
    getStatusClass(id) {
      let statusId = id;
      if (id === this.$statusUtil.getStatusId('delivered')) {
        statusId = this.$statusUtil.getStatusId('delivery_pending');
      }
      const status = this.indentsStatus.find(status => status.status_id === statusId);
      const color = status && status.color;
      if (color && color.startsWith('#')) {
        return { backgroundColor: color };
      }
      return `status-${color}`;
    },
    getMainStatusId(id) {
      let mainStatusId = this.$statusUtil.getMainStatusId(id);
      if (!this.$hasFeature('transporter_delivery_pending_status')
        && mainStatusId === this.$statusUtil.getStatusId('delivery_pending')) {
        mainStatusId = this.$statusUtil.getStatusId('delivered');
      }
      return mainStatusId;
    },
    async openResubmitDialog() {
      const dest = this.firstAiRejectedDestination;
      if (!dest) return;
      const destName = (dest.details && dest.details.name) || dest.location_id;
      try {
        const hasConfirm = await this.$PromptConfirm(
          this.$t('POD_AI_RETRIGGER_CONFIRM_MSG', { destName }),
          this.$t('POD_AI_RETRIGGER_TITLE'),
          {
            confirmButtonText: 'Retrigger',
            cancelButtonText: 'Cancel',
            customClass: 'pod-ai-retrigger-confirm',
          },
        );
        if (hasConfirm === 'confirm') {
          await this.$store.dispatch('podAiValidation/resubmit', {
            orderNo: this.indent.order_no,
            destinationId: dest.location_id,
          });
          this.$successMessage({ message: this.$t('POD_AI_RETRIGGER_SUCCESS') });
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$errorMessage({ message: err?.data?.error || 'Failed to resubmit POD validation' });
        }
      }
    },
    openWorkflowActions() {
      try {
        let action = {};
        switch (this.indent && this.indent.next_event) {
          case 'VEHICLE_ASSIGNMENT': {
            action = transporterActions.assign_truck;
            break;
          }
          case 'TRUCK_IN':
            action = transporterActions.truck_in;
            break;
          case 'TRUCK_OUT': {
            action = transporterActions.truck_out;
            break;
          }
          case 'BOOKING_CONFIRMATION': {
            action = transporterActions.booking_confirmation;
            break;
          }
          case 'MARK_DELIVER': {
            action = transporterActions.mark_as_delivered;
            break;
          }
          default:
            break;
        }
        this.showActions({ action });
      } catch (error) {
        this.$errorMessage({ message: this.$getErr('APP_CATCH_ERR') });
      }
    },
  },
};
</script>
<style lang="scss">
@import "~@common/styles-shipper/settings";

.datelabel,.text-secondary {
  display: block;
  font-family: $fontRegular;
  font-size: 12px;
  color: #737373;
  padding: 7px 0 0 0;
}
</style>
